import React, { InputHTMLAttributes, useCallback, useEffect, useState, type ComponentProps } from "react";
import { AddImage, Close } from "../../Icons";
import style from "./Inputs.module.css";
import { FileType, InputFileType, readImageAsDataURL } from "./utils";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, rectSortingStrategy } from "@dnd-kit/sortable";
import { SortableItem } from "../SortableItem";
import { GridContainer } from "./GridContainer";
import { ChangeEventHandler } from "react";
import { UseFormSetError } from "react-hook-form";

function InputFile({
  label,
  /**
   *  contains only the new images to be inserted
   */
  value,
  extraProps,
  onChange,
  onAddToDeletedFiles,
  accept,
  error,
  disabled,
  maximumFileCount,
  maximumFileSize,
  setFieldError,
  name,
}: InputFileProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [currentFiles, setCurrentFiles] = useState<FileType[]>(() => {
    return value.map(({ id, file }) => {
      return {
        id,
        type: "link",
        src: file as string,
      };
    });
  });

  const onDeleteFileHandler = useCallback(
    (fileIndex: number) => {
      if (currentFiles[fileIndex].type === "link") {
        onAddToDeletedFiles(currentFiles[fileIndex].id as number);
      }
      onChange([...value.filter((file) => file.id !== currentFiles[fileIndex].id)]);
      setCurrentFiles((current) => current.filter((_, index) => fileIndex !== index));
    },
    [currentFiles, onAddToDeletedFiles, onChange, value],
  );

  const onUploadFileHandler = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (setFieldError && name) {
        setFieldError(name, {
          message: undefined,
        });
      }
      const eventsFiles = e.target.files;
      if (eventsFiles && eventsFiles.length > 0) {
        // enforce maximum files count
        if (maximumFileCount && eventsFiles.length + value.length > maximumFileCount) {
          if (setFieldError && name) {
            const errorMessage = `maximum files count is ${maximumFileCount},
                                          you already picked ${value.length},
                                          you could pick ${maximumFileCount - value.length} more files.
                                          `;
            setFieldError(name, {
              message: errorMessage,
            });
          }
          return;
        }
        // enforce max file size in byte
        if (maximumFileSize) {
          const fileNames: string[] = [];
          Array.from(eventsFiles).forEach((f) => {
            if (f.size > maximumFileSize) fileNames.push(f.name);
          });
          if (fileNames.length) {
            if (setFieldError && name) {
              const errorMessage = `every file size in the following list exceeds the maximum file size which is ${
                maximumFileSize * 1e-6
              } mb: ${fileNames.join(", ")}`;
              setFieldError(name, {
                message: errorMessage,
              });
            }
            return;
          }
        }
        let timeStamp = Date.now();
        const files: FileType[] = [];
        if (accept === "pdf") {
          files.push(
            ...Array.from(e.target.files as FileList).map((file, index) => ({
              id: timeStamp + index,
              type: "file" as "file",
              src: "",
              name: file.name.length > 40 ? file.name.substring(0, 40) + "..." : file.name,
            })),
          );
        } else {
          const readFiles = Array.from(e.target.files as FileList).map((file) => readImageAsDataURL(file));
          const values = await Promise.all(readFiles);
          files.push(
            ...values.map((val, index) => ({
              id: timeStamp + index,
              type: "file" as "file",
              src: val as string,
            })),
          );
        }
        setCurrentFiles((current) => [...(extraProps?.multiple ? current : []), ...files]);
        onChange([
          ...value,
          ...Array.from(e.target.files as FileList).map((file, index) => ({
            id: timeStamp + index,
            file,
          })),
        ]);
      }
    },
    [setFieldError, name, maximumFileCount, value, maximumFileSize, accept, onChange, extraProps?.multiple],
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over === null) return;
    if (active.id !== over.id) {
      setCurrentFiles((files) => {
        const oldIndex = files.findIndex((item) => item.id == active.id);
        const newIndex = files.findIndex((item) => item.id == over.id);

        const fs = arrayMove(files, oldIndex, newIndex);
        return fs;
      });
      const oldIndex = value.findIndex((item) => item.id == active.id);
      const newIndex = value.findIndex((item) => item.id == over.id);

      onChange(arrayMove(value, oldIndex, newIndex));
    }
  }

  return (
    <div className={style.input_file}>
      <div className={style.input_file_box}>
        <div>
          <input
            {...extraProps}
            type="file"
            disabled={disabled}
            accept={accept === "pdf" ? "application/pdf" : "image/png,image/jpg,image/jpeg"}
            onChange={onUploadFileHandler}
          />
          {(extraProps?.multiple ? true : !currentFiles[0]) && <AddImage width="40" height="40" color="#959595" />}
          {(extraProps?.multiple ? true : !currentFiles[0]) && <label>{label}</label>}
          {!extraProps?.multiple && currentFiles[0] && <img src={currentFiles[0].src} alt="" />}
        </div>
      </div>

      <div
        style={{
          width: "100%",
        }}
      >
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={currentFiles} strategy={rectSortingStrategy}>
            <GridContainer columns={4}>
              {currentFiles.map((file, index) => {
                return (
                  <SortableItem id={file.id} key={file.id}>
                    <div key={index} className={style.input_file_image}>
                      <div>
                        <button type="button" onClick={() => onDeleteFileHandler(index)}>
                          <Close width="12" height="12" color="#fff" />
                        </button>
                        {accept === "pdf" ? <></> : <img src={file.src} alt="" />}
                      </div>
                    </div>
                  </SortableItem>
                );
              })}
            </GridContainer>
          </SortableContext>
        </DndContext>
      </div>
      {error && (
        <span className={style.input_note} style={{ color: "#dc0000" }}>
          {error}
        </span>
      )}
    </div>
  );
}

export default InputFile;

interface InputFileProps {
  onChange: (files: { id: number; file: File | string }[]) => void;
  value: {
    id: number;
    file: File | string;
  }[];
  onAddToDeletedFiles: (id: number) => void;
  props?: InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
  error?: string;
  note?: string;
  disabled?: boolean;
  label: string;
  accept: "image" | "pdf";
  extraProps?: any;
  maximumFileCount?: number;
  /**
   * size in Bytes
   */
  maximumFileSize?: number;
  setFieldError?: UseFormSetError<any>;
  /**
   * filed name in the form, will mostly be used to set the errors
   */
  name?: string;
}
